import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropertyCard from '../components/PropertyCard';
import PropertySearch from '../components/PropertySearch';
import { loadInitialProperties } from '../utils/propertyStorage';

const PropertyListing = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [initialFilters, setInitialFilters] = useState({});
  const [allProperties, setAllProperties] = useState([]);

  // Učitaj sve nekretnine
  useEffect(() => {
    const loadedProperties = loadInitialProperties();
    setAllProperties(loadedProperties);
    setFilteredProperties(loadedProperties);
  }, []);

  // Memoiziramo funkciju za pretragu
  const handleSearch = useCallback((filters) => {
    setIsSearching(true);
    
    // Ako nema filtera, prikaži sve nekretnine
    if (!filters.location && !filters.minPrice && !filters.maxPrice && !filters.bedrooms) {
      setFilteredProperties(allProperties);
      setIsSearching(false);
      return;
    }

    // Filtriraj nekretnine
    const filtered = allProperties.filter(property => {
      const locationMatch = filters.location 
        ? property.location.toLowerCase().includes(filters.location.toLowerCase())
        : true;
      
      const minPriceMatch = filters.minPrice 
        ? property.price >= parseInt(filters.minPrice)
        : true;
      
      const maxPriceMatch = filters.maxPrice 
        ? property.price <= parseInt(filters.maxPrice)
        : true;
      
      const bedroomsMatch = filters.bedrooms 
        ? property.bedrooms >= parseInt(filters.bedrooms)
        : true;
      
      return locationMatch && minPriceMatch && maxPriceMatch && bedroomsMatch;
    });
    
    setFilteredProperties(filtered);
    setIsSearching(false);
  }, [allProperties]);

  // Funkcija za generiranje teksta o rezultatima
  const getResultsText = () => {
    const count = filteredProperties.length;
    
    if (count === 0) return 'Nema pronađenih nekretnina';
    if (count === 1) return 'Pronađena 1 nekretnina';
    if (count < 5) return `Pronađene ${count} nekretnine`;
    return `Pronađeno ${count} nekretnina`;
  };

  // Reset filtera
  const handleResetFilters = () => {
    navigate('/nekretnine');
    setFilteredProperties(allProperties);
  };

  // Postavi početne filtere iz URL-a
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filters = {
      location: params.get('location') || '',
      minPrice: params.get('minPrice') || '',
      maxPrice: params.get('maxPrice') || '',
      bedrooms: params.get('bedrooms') || ''
    };
    
    setInitialFilters(filters);
    handleSearch(filters);
  }, [location.search, handleSearch]);

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Pretražite naše nekretnine</h1>
      
      <div style={styles.searchContainer}>
        <PropertySearch 
          onSearch={handleSearch} 
          initialFilters={initialFilters} 
          onReset={handleResetFilters}
        />
      </div>
      
      {isSearching ? (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p>Pretraga u tijeku...</p>
        </div>
      ) : (
        <>
          <div style={styles.resultsHeader}>
            <h2 style={styles.resultsTitle}>{getResultsText()}</h2>
            {filteredProperties.length > 0 && (
              <button 
                onClick={handleResetFilters} 
                style={styles.resetButton}
              >
                Poništi filtere
              </button>
            )}
          </div>
          
          {filteredProperties.length === 0 ? (
            <div style={styles.noResults}>
              <h3>Nema rezultata za vašu pretragu</h3>
              <p>Pokušajte promijeniti kriterije pretrage ili poništite filtere</p>
              <button 
                onClick={handleResetFilters} 
                style={styles.bigResetButton}
              >
                Poništi sve filtere
              </button>
            </div>
          ) : (
            <div style={styles.grid}>
              {filteredProperties.map(property => (
                <PropertyCard 
                  key={property.id} 
                  property={property} 
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
    minHeight: '80vh'
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    marginBottom: '20px',
    color: '#2c3e50',
    textAlign: 'center'
  },
  searchContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    padding: '20px',
    marginBottom: '30px'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '50px',
    textAlign: 'center'
  },
  spinner: {
    border: '5px solid rgba(0, 0, 0, 0.1)',
    borderTopColor: '#4a6fa5',
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px'
  },
  resultsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    paddingBottom: '15px',
    borderBottom: '1px solid #eee'
  },
  resultsTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#2c3e50'
  },
  resetButton: {
    padding: '8px 16px',
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: '#d0d0d0'
    }
  },
  noResults: {
    textAlign: 'center',
    padding: '40px',
    backgroundColor: '#f9f9f9',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
  },
  bigResetButton: {
    padding: '12px 25px',
    backgroundColor: '#4a6fa5',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    marginTop: '20px',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: '#3a5a80'
    }
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '25px',
    marginBottom: '40px'
  }
};

export default PropertyListing;